/**
 * All tunables in one place, plus the URL contract other apps use to launch us.
 *
 * The spawn is deliberately *not* a constant: the sibling `smartmaps` app picks
 * a coordinate and links here, so everything about where and how the player
 * enters the world has to be expressible as a query string.
 *
 *   /?lat=57.673325&lon=12.252577&alt=2500&mode=parachute&heading=90
 */

import type { HouseDetail } from './core/build/houses.js';
import type { TileKey } from './core/geo/tilekey.js';

/** How the player enters and moves. */
export type PlayerMode = 'walk' | 'freefall' | 'drive' | 'moto' | 'heli';

export interface SpawnConfig {
  lat: number;
  lon: number;
  /** Metres above ground level. `0` means stand on the terrain. */
  altitudeAgl: number;
  mode: PlayerMode;
  /** Compass heading in degrees clockwise from north. */
  heading: number;
  /** Which two-wheeler a `moto` start conjures: the motocrosser unless the link asked for the e-scooter. */
  vehicle?: 'scooter';
}

/**
 * Level of detail, as a distance-driven quadtree rather than fixed rings.
 *
 * Fixed rings are tempting but do not tile: a 5x5 block of z14 tiles spans
 * ~6.5 km while a single z12 tile spans ~5.2 km, so no coarse tile is ever
 * fully covered by the fine ring. The two surfaces overlap and z-fight. Rings
 * only work when each ring is a whole number of coarser tiles.
 *
 * Instead: start from a block of `minZoom` tiles and recursively split any tile
 * the player is close to, down to `maxZoom`. Every leaf is drawn exactly once,
 * so the cover is exact by construction — no overlaps, no gaps.
 */
export interface LodConfig {
  /** Coarsest zoom. With `radiusTiles`, this sets how far the world extends. */
  minZoom: number;
  /** Finest zoom terrain will subdivide to. */
  maxZoom: number;
  /** Half-width of the loaded region, in tiles at `minZoom`. */
  radiusTiles: number;
  /**
   * Split a tile when the player is within `splitFactor` tile-widths of it.
   * ~1.0 gives roughly one LOD step per tile-width of distance.
   */
  splitFactor: number;
  /**
   * Mesh vertices per axis for a tile at `maxZoom` — the ground under the
   * player. 96 => a 97x97 grid, ~13.6 m between vertices on a z14 tile.
   */
  gridRes: number;
  /**
   * Mesh vertices per axis for every coarser level.
   *
   * Coarse tiles are, by construction, the ones far away or far below, so they
   * do not need the same vertex budget. Uniform density across levels costs
   * more than twice the triangles for detail nobody can resolve. Their edges
   * then disagree with finer neighbours, but that is already true between any
   * two zoom levels and is exactly what the skirts cover.
   */
  coarseGridRes: number;
  /**
   * Skirt depth as a multiple of the tile's cell width, with `minSkirt` as a
   * floor. Scaling with the tile means coarse tiles — where neighbouring LODs
   * disagree most — get correspondingly deeper aprons.
   */
  skirtFactor: number;
  minSkirt: number;
}

export interface WorldConfig {
  spawn: SpawnConfig;
  lod: LodConfig;
  /**
   * Detail level for vector features.
   *
   * A fixed single zoom — `minZoom === maxZoom` — because the vector source
   * only goes to z14 and trees are not worth drawing beyond a few kilometres
   * anyway. `radiusTiles` is therefore the tree draw distance, in z14 tiles.
   */
  vectorLod: LodConfig;
  /** Trees per tile ceiling, so one heavily wooded tile cannot stall a frame. */
  maxTreesPerTile: number;
  /** Global multiplier on tree density — the main vegetation quality knob. */
  treeDensityScale: number;
  /** Longest run between road cross-sections, in metres. */
  roadMaxSegment: number;
  /** Skip roads narrower than this half-width. 0 draws every footpath. */
  minRoadHalfWidth: number;
  /**
   * How much geometry each building is worth: `0` walls and a roof, `1` adds
   * windows and a door, `2` adds chimneys, canopies and corner boards.
   *
   * The main quality-for-vertices knob on the built environment. A dense z14
   * tile holds a few hundred buildings, so the step from `0` to `2` is worth
   * roughly a hundred thousand vertices a tile — free on a desktop GPU, and
   * the first thing to drop on a phone.
   */
  houseDetail: HouseDetail;
  /**
   * Rendering effects. Soft sun shadows and ambient occlusion are most of the
   * diorama look and most of its cost; `?fx=low` turns both off for a weak
   * GPU, and for the headless smoke runs, which render in software. A phone
   * gets its own profile (`PHONE`): see `configFromSearch`.
   */
  graphics: Graphics;
  /** Villagers kept walking the streets around the player. 0 empties the town. */
  villagers: number;
  /** Most cars kept driving the bigger roads around the player. 0 empties the roads. */
  traffic: number;
  /**
   * Fetch OSM building tags — type, height, storeys, roof shape, age, colour
   * — from Overpass to shape each building. `?buildings=osm` (default) or
   * `?buildings=off`, which builds from outlines alone.
   */
  buildingTags: boolean;
  vectorMemoryBudgetBytes: number;
  elevation: {
    urlTemplate: string;
    minZoom: number;
    maxZoom: number;
    tileSize: number;
    attribution: string;
    /**
     * See `ElevationSource.overlay`: Lantmäteriet's laser ground model over
     * Sweden, the tiles `tools/lmdem.mjs` made, where its coverage list
     * (`index`) has them. `?dem=off` turns it off.
     */
    overlay?: { urlTemplate: string; zoom: number; attribution: string; index?: string };
  };
  vector: {
    urlTemplate: string;
    minZoom: number;
    maxZoom: number;
    attribution: string;
    /**
     * Sweden's own Shortbread tiles (`tools/osmsweden`), where their coverage
     * list (`index`) has them, at `zoom`: the same layers, and in them what
     * Overpass would otherwise be asked for. `?vector=osm` turns it off.
     * `buildingsAttribution` credits Lantmäteriet's footprints in them.
     */
    overlay?: { urlTemplate: string; zoom: number; index: string; buildingsAttribution?: string };
  };
  /**
   * Sweden's forest from Naturvårdsverket's NMD, the tiles `tools/nmdtrees`
   * made, where its coverage list (`index`) has them: there the trees are
   * scattered from it instead of from OpenStreetMap's forest. `?forest=osm`
   * turns it off. `trees`: where Skogsstyrelsen's tree height has been made
   * single trees, those stand instead of the cells' own (`?forest=nmd` for
   * the cells alone).
   */
  forest?: {
    urlTemplate: string; zoom: number; attribution: string; index: string;
    trees?: { urlTemplate: string; index: string; attribution: string };
  };
  /**
   * Re-anchor the ENU frame once the player is this far from the origin, in
   * metres. Kept tight because the tangent plane's east-west scale error is
   * proportional to this distance (~0.25% at 10 km), and rebasing costs only a
   * recompute of chunk transforms.
   */
  rebaseThreshold: number;
  concurrency: { elevation: number; vector: number };
  /** Soft cap on resident decoded tile bytes before LRU eviction kicks in. */
  memoryBudgetBytes: number;
}

export const DEFAULT_SPAWN: SpawnConfig = {
  // Västergötland, west of Borås. Ground here is ~108 m; the z14 tile is
  // 14/8749/4962 and holds forest, farmland, grass, meadow and streams.
  lat: 57.673325,
  lon: 12.252577,
  altitudeAgl: 0,
  mode: 'walk',
  heading: 0,
};

/**
 * Both sources are keyless, globally available and send
 * `Access-Control-Allow-Origin: *`. They sit behind the source interfaces in
 * `core/sources/` so a keyed provider can replace either one without the
 * pipeline noticing. See docs/data-sources.md.
 */
export interface Graphics {
  shadows: boolean;
  ambientOcclusion: boolean;
  /** The occlusion's resolution, a fraction of the frame's: 0.5 shades a quarter of the pixels. */
  aoScale: number;
  /** MSAA samples on the scene's render target: 4, or 0 for none. */
  msaa: number;
  /** A screen-space smoothing pass on the final image: next to nothing to run, where MSAA is too dear. */
  fxaa: boolean;
  /** The sun's shadow map, texels a side, and how far round the player it reaches, metres. */
  shadowMapSize: number;
  shadowExtent: number;
  /** Most device pixels drawn per CSS pixel, before the render scale. */
  maxPixelRatio: number;
  /** Whether small things — lamps, fences, bins, parked cars — cast shadows, or only trees and buildings. */
  propShadows: boolean;
  /**
   * Bytes of a new tile's GPU buffers shown a frame: a tile's meshes are
   * uploaded as they are first drawn, and a whole one at once is a freeze.
   * A city block tile is 25-30 MB of buildings: at 8 MB it goes up over
   * three or four frames, not in one jerk.
   */
  uploadBudget: number;
  /** The near terrain as separate facets, each its own shade: three times the vertices. */
  facetTerrain: boolean;
  /** Name boards' resolution, 1 in full. See `setSignScale`. */
  signScale: number;
}

/** Everything, on a desktop or laptop GPU. */
export const FULL_GRAPHICS: Graphics = {
  shadows: true, ambientOcclusion: true, aoScale: 1, msaa: 4, fxaa: false, shadowMapSize: 2048, shadowExtent: 110,
  maxPixelRatio: 2, propShadows: true, uploadBudget: 8_000_000, facetTerrain: true, signScale: 1,
};

/**
 * A dense screen — a 4K laptop panel at 200 % — on a desktop GPU. At its full
 * ratio of 2 the frame is 4.4 times 1080p's pixels, and every one of them is
 * shaded, occluded and multisampled: the frame rate goes with the pixels, not
 * the triangles. Drawn at 1.25 device pixels a CSS pixel it still looks
 * sharp; the occlusion runs at half that, and FXAA smooths the edges in
 * place of MSAA, which at this density is mostly paying for its resolve.
 */
export const DENSE_GRAPHICS: Graphics = {
  ...FULL_GRAPHICS, maxPixelRatio: 1.25, aoScale: 0.5, msaa: 0, fxaa: true,
};

/**
 * A phone's GPU — a Pixel's Mali, an iPhone's — draws tile by tile in fast
 * on-chip memory, and everything that reads the whole frame back costs it
 * dearly: the ambient occlusion passes, resolving MSAA, a big shadow map
 * drawn again every frame. Its screen is so dense that edges hardly need
 * smoothing and 1.5 device pixels a CSS pixel look as sharp as 3. So: no
 * occlusion, no MSAA, a smaller, shorter shadow cast only by trees and
 * buildings, and the pixel ratio capped at 1.5. And no freezes as tiles come
 * in: their meshes shown three megabytes a frame, and the near terrain kept
 * indexed, not split into facets on the main thread.
 */
export const PHONE_GRAPHICS: Graphics = {
  shadows: true, ambientOcclusion: false, aoScale: 1, msaa: 0, fxaa: false, shadowMapSize: 1024, shadowExtent: 70,
  maxPixelRatio: 1.5, propShadows: false, uploadBudget: 3_000_000, facetTerrain: false, signScale: 0.5,
};

/**
 * The world on a phone: the graphics above, and less of it to draw — trees
 * at under half the density, houses as simple blocks with roofs, fewer
 * people, and a coarser terrain mesh near the player. The phone's frame is
 * mostly triangles, and most of the triangles are trees.
 */
export function phoneProfile(base: WorldConfig): WorldConfig {
  return {
    ...base,
    lod: { ...base.lod, gridRes: Math.min(base.lod.gridRes, 160) },
    maxTreesPerTile: Math.min(base.maxTreesPerTile, 3500),
    treeDensityScale: base.treeDensityScale * 0.55,
    houseDetail: Math.min(base.houseDetail, 1) as HouseDetail,
    graphics: PHONE_GRAPHICS,
    villagers: Math.min(base.villagers, 8),
    traffic: Math.min(base.traffic, 24),
  };
}

/**
 * Where the Lantmäteriet ground model's tiles are: the R2 bucket the pipeline
 * fills (docs/lantmateriet-pipeline.md), or another place set at build time
 * with VITE_DEM_BASE (`/dem/lm` for tiles under the site's own public/).
 */
const DEM_BASE = ((import.meta.env?.VITE_DEM_BASE as string | undefined) ?? 'https://dem.toraxe.com/lm').replace(/\/$/, '');
/** Where the NMD forest tiles are (docs/mapdetail.md, "Forest from NMD"), beside the ground model's; VITE_FOREST_BASE to move them. */
const FOREST_BASE = ((import.meta.env?.VITE_FOREST_BASE as string | undefined) ?? 'https://dem.toraxe.com/nmd').replace(/\/$/, '');
/** Where the Sweden vector tiles are (docs/mapdetail.md, "Sweden's own map tiles"); VITE_OSM_BASE to move them. */
const OSM_BASE = ((import.meta.env?.VITE_OSM_BASE as string | undefined) ?? 'https://dem.toraxe.com/osm').replace(/\/$/, '');
const TREES_BASE = ((import.meta.env?.VITE_TREES_BASE as string | undefined) ?? 'https://dem.toraxe.com/sks').replace(/\/$/, '');
/** Sweden's bus routes, beside its map tiles: `routes/12/x/y.json` (tools/osmsweden/routes.py). */
export const OSM_ROUTES_BASE = `${OSM_BASE}/routes`;

export const DEFAULT_CONFIG: WorldConfig = {
  spawn: DEFAULT_SPAWN,
  lod: {
    // 3x3 z10 tiles ~= 63 km of world at this latitude, subdividing down to
    // z14 near the player. z14 is the ceiling because it is the vector
    // source's max zoom, and gives ~5 m/px of elevation. The wide coarse
    // extent exists for the parachute entry, where the horizon is the view.
    minZoom: 10,
    maxZoom: 14,
    radiusTiles: 1,
    splitFactor: 1.0,
    // ~5 m between vertices on the nearest tiles, the elevation data's own
    // pixel, lightly smoothed. At 7 m the laser ground model's cuttings and
    // banks came out in sharp facets along every road; this keeps a little of
    // the faceted look without them. 252 is the most that keeps a tile's
    // vertices, skirt and all, within 16-bit indices.
    gridRes: 252,
    coarseGridRes: 32,
    skirtFactor: 2.0,
    minSkirt: 20,
  },
  vectorLod: {
    minZoom: 14,
    maxZoom: 14,
    radiusTiles: 1,
    splitFactor: 0,
    gridRes: 96,
    coarseGridRes: 32,
    skirtFactor: 0,
    minSkirt: 0,
  },
  maxTreesPerTile: 9000,
  treeDensityScale: 1,
  roadMaxSegment: 10,
  minRoadHalfWidth: 0.7,
  houseDetail: 2,
  graphics: FULL_GRAPHICS,
  villagers: 14,
  traffic: 72,
  buildingTags: true,
  vectorMemoryBudgetBytes: 128 * 1024 * 1024,
  elevation: {
    urlTemplate: 'https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png',
    minZoom: 0,
    maxZoom: 15,
    tileSize: 256,
    attribution: 'Elevation: AWS Terrain Tiles / Mapzen',
    overlay: { urlTemplate: `${DEM_BASE}/{z}/{x}/{y}.png`, zoom: 14, attribution: 'Markhöjdmodell grid 1+ © Lantmäteriet, processed, CC BY 4.0', index: `${DEM_BASE}/index.json` },
  },
  vector: {
    urlTemplate: 'https://vector.openstreetmap.org/shortbread_v1/{z}/{x}/{y}.mvt',
    minZoom: 0,
    maxZoom: 14,
    attribution: '© OpenStreetMap contributors',
    overlay: {
      urlTemplate: `${OSM_BASE}/{z}/{x}/{y}.mvt`, zoom: 14, index: `${OSM_BASE}/index.json`,
      buildingsAttribution: 'Byggnader: Topografi 10 © Lantmäteriet, processed, CC BY 4.0',
    },
  },
  forest: {
    urlTemplate: `${FOREST_BASE}/{z}/{x}/{y}.webp`, zoom: 14, attribution: 'Nationella marktäckedata 2023 © Naturvårdsverket, CC0', index: `${FOREST_BASE}/index.json`,
    trees: { urlTemplate: `${TREES_BASE}/{z}/{x}/{y}.trees`, index: `${TREES_BASE}/index.json`, attribution: 'Trädhöjd © Skogsstyrelsen, CC0' },
  },
  rebaseThreshold: 10_000,
  concurrency: { elevation: 6, vector: 4 },
  memoryBudgetBytes: 384 * 1024 * 1024,
};

/** Expand a `{z}/{x}/{y}` template. */
export function tileUrl(template: string, key: TileKey): string {
  return template
    .replace('{z}', String(key.z))
    .replace('{x}', String(key.x))
    .replace('{y}', String(key.y));
}

/**
 * Spellings accepted in the `mode` query parameter.
 *
 * Deliberately generous: these links are written by hand and by other
 * applications, and the difference between "parachute" and "freefall" is an
 * internal naming detail nobody outside this codebase should have to know.
 */
const MODE_ALIASES: Record<string, PlayerMode> = {
  walk: 'walk',
  foot: 'walk',
  parachute: 'freefall',
  freefall: 'freefall',
  skydive: 'freefall',
  drive: 'drive',
  car: 'drive',
  moto: 'moto',
  motocross: 'moto',
  motorbike: 'moto',
  motorcycle: 'moto',
  bike: 'moto',
  dirtbike: 'moto',
  mx: 'moto',
  // The e-scooter rides in the motorbike's mode: see `SCOOTER_MODES`.
  scooter: 'moto',
  escooter: 'moto',
  kukirin: 'moto',
  heli: 'heli',
  helicopter: 'heli',
  chopper: 'heli',
  fly: 'heli',
};

/** Spellings of `mode` that start on the e-scooter rather than the motocrosser. */
const SCOOTER_MODES: ReadonlySet<string> = new Set(['scooter', 'escooter', 'kukirin']);

function num(params: URLSearchParams, name: string, fallback: number): number {
  const raw = params.get(name);
  // `Number('')` and `Number('   ')` are both 0, so an empty param would
  // otherwise read as a real coordinate of zero rather than as "not supplied".
  if (raw === null || raw.trim() === '') return fallback;
  const value = Number(raw);
  return Number.isFinite(value) ? value : fallback;
}

/**
 * Read a spawn out of a query string.
 *
 * Unknown or malformed values fall back rather than throwing — a bad inbound
 * link should drop the player somewhere sensible, not show a blank screen.
 */
export function spawnFromSearch(search: string): SpawnConfig {
  const params = new URLSearchParams(search);
  const lat = Math.min(85, Math.max(-85, num(params, 'lat', DEFAULT_SPAWN.lat)));
  const lon = num(params, 'lon', DEFAULT_SPAWN.lon);

  const rawMode = (params.get('mode') ?? '').toLowerCase();
  // `parachute` is the word a linking app would use; `freefall` is what the
  // controller calls the state. See MODE_ALIASES.
  const mode = MODE_ALIASES[rawMode] ?? 'walk';

  // Entries that begin airborne need a default altitude, or they spawn buried
  // in the ground and immediately settle.
  const defaultAlt = mode === 'freefall' ? 2500 : mode === 'heli' ? 250 : 0;

  return {
    lat,
    lon,
    altitudeAgl: Math.max(0, num(params, 'alt', defaultAlt)),
    mode,
    heading: ((num(params, 'heading', DEFAULT_SPAWN.heading) % 360) + 360) % 360,
    ...(SCOOTER_MODES.has(rawMode) ? { vehicle: 'scooter' as const } : {}),
  };
}

/**
 * The config for a visit. A phone — `phone` true, a touch screen — gets the
 * phone profile (`phoneProfile`) unless `?fx=high` asks for everything;
 * `?fx=phone` gives any device the phone's. A dense screen otherwise —
 * `dense` true — gets `DENSE_GRAPHICS`, as `?fx=dense` gives any. `?fx=low`
 * and `?fx=medium` then turn effects off on top of whichever it is.
 */
export function configFromSearch(search: string, base: WorldConfig = DEFAULT_CONFIG, phone = false, dense = false): WorldConfig {
  const params = new URLSearchParams(search);
  const fx = (params.get('fx') ?? '').toLowerCase();
  const full = fx === 'high' || fx === 'full';
  if (fx === 'phone' || (phone && !full)) base = phoneProfile(base);
  else if (fx === 'dense' || (dense && !full)) base = { ...base, graphics: DENSE_GRAPHICS };
  const graphics = fx === 'low'
    ? { ...base.graphics, shadows: false, ambientOcclusion: false }
    : fx === 'medium' ? { ...base.graphics, ambientOcclusion: false } : base.graphics;
  const villagers = Math.max(0, Math.min(80, Math.round(num(params, 'villagers', base.villagers))));
  const traffic = Math.max(0, Math.min(200, Math.round(num(params, 'traffic', base.traffic))));
  const buildings = (params.get('buildings') ?? '').toLowerCase();
  const buildingTags = buildings === 'off' || buildings === 'none' || buildings === '0'
    ? false
    : buildings === 'osm' ? true : base.buildingTags;
  // The Lantmäteriet ground model's tiles under the site's own /dem/lm, where
  // `tools/lmdem.mjs` has made them; `?dem=off` for the AWS tiles alone.
  const dem = (params.get('dem') ?? '').toLowerCase();
  const elevation = dem === 'off' || dem === 'aws' ? { ...base.elevation, overlay: undefined } : base.elevation;
  // `?forest=osm` (or off) for OpenStreetMap's forest in Sweden too.
  const forestParam = (params.get('forest') ?? '').toLowerCase();
  const forest = forestParam === 'osm' || forestParam === 'off' ? undefined
    : forestParam === 'nmd' && base.forest !== undefined ? { ...base.forest, trees: undefined } : base.forest;
  // `?vector=osm` for vector.openstreetmap.org's tiles in Sweden too.
  const vector = (params.get('vector') ?? '').toLowerCase() === 'osm' ? { ...base.vector, overlay: undefined } : base.vector;
  return { ...base, spawn: spawnFromSearch(search), graphics, villagers, traffic, buildingTags, elevation, forest, vector };
}
